/**
 * @fileoverview NumberedListRenderer component for the Storyteller web application.
 * Renders a numbered list with items from the schema.
 *
 * @example
 * // Basic usage
 * <NumberedListRenderer
 *   schema={{
 *     options: { items: ['item1', 'item2', 'item3'] }
 *   }}
 *   uischema={{ options: { items: ['item1', 'item2', 'item3'] } }}
 * />
 */

import { OwnPropsOfRenderer, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsDetailProps } from '@jsonforms/react'
import { VanillaRendererProps, withVanillaControlProps } from '@jsonforms/vanilla-renderers'
import { useTranslation } from 'react-i18next'

import css from './NumberedListRenderer.module.css'

/**
 * @function NumberedListRenderer
 * @description Component for rendering a numbered list with items from the schema.
 * @param {OwnPropsOfRenderer & VanillaRendererProps} props - The props for the NumberedListRenderer component.
 * @returns {JSX.Element} The NumberedListRenderer component.
 */
function NumberedListRenderer(props: OwnPropsOfRenderer & VanillaRendererProps) {
    const { t } = useTranslation()
    return (
        <div className={css.container}>
            {(props.uischema.options.items as string[]).map((item: string, i: number) => (
                <div className={css.row} key={item}>
                    <span className={css.numeric}>{i + 1}</span>
                    <span>{t(item)}</span>
                </div>
            ))}
        </div>
    )
}

export const NumberedListTester = rankWith(3, uiTypeIs('NumberedList'))

export default withVanillaControlProps(withJsonFormsDetailProps(NumberedListRenderer))
